/**
 * CellX Quiz Framework
 * ─────────────────────────────────────────────────────────────
 * Config-driven quiz engine for lead capture + product recommendation.
 * Reusable across unlimited quizzes — each quiz is a JS config file.
 *
 * Usage:
 *   1. Register:  CellXQuiz.register({ id: 'my-quiz', steps: [...], recommend: fn })
 *   2. Open:      CellXQuiz.open('my-quiz')
 *   3. Close:     CellXQuiz.close()
 *
 * Config schema:
 *   {
 *     id:        string,                    // unique quiz identifier
 *     intro:     { headline, subtext, image, cta },       // optional intro screen
 *     steps:     [{ id, question, type, options }],       // 'single' | 'multi'
 *     email:     { enabled, headline, subtext, ... },     // optional email capture
 *     recommend: function(answers) → { products, headline, subtext, insight },
 *     results:   { cta_single, cta_all, retake_label }    // results screen config
 *   }
 * ─────────────────────────────────────────────────────────────
 */
(function() {
  'use strict';

  var _quizzes = {};
  var _state = null;
  var _modal = null;
  var _content = null;

  // ── PUBLIC API ──────────────────────────────────────────

  function register(config) {
    if (!config || !config.id) throw new Error('[CellXQuiz] Config must have an id');
    _quizzes[config.id] = config;
  }

  function open(id) {
    var quiz = _quizzes[id];
    if (!quiz) { console.warn('[CellXQuiz] Quiz not found:', id); return; }
    _state = {
      quiz: quiz,
      step: quiz.intro ? -1 : 0,
      answers: {},
      result: null,
      products: [],
      email: ''
    };
    _ensureModal();
    _render();
    _modal.classList.add('is-open');
    document.body.style.overflow = 'hidden';
  }

  function close() {
    if (_modal) _modal.classList.remove('is-open');
    document.body.style.overflow = '';
    _state = null;
  }

  // ── MODAL SETUP ─────────────────────────────────────────

  function _ensureModal() {
    if (_modal) return;
    _modal = document.getElementById('cellx-quiz-modal');
    if (!_modal) {
      // Fallback: create modal if snippet wasn't rendered
      _modal = document.createElement('div');
      _modal.id = 'cellx-quiz-modal';
      _modal.className = 'cxq';
      _modal.innerHTML =
        '<div class="cxq__overlay"></div>' +
        '<div class="cxq__dialog">' +
          '<button class="cxq__close" aria-label="Cerrar">&times;</button>' +
          '<div class="cxq__body"></div>' +
        '</div>';
      document.body.appendChild(_modal);
    }
    _content = _modal.querySelector('.cxq__body');
    _modal.querySelector('.cxq__overlay').addEventListener('click', close);
    _modal.querySelector('.cxq__close').addEventListener('click', close);
  }

  // ── RENDER ROUTER ───────────────────────────────────────

  function _render() {
    if (!_state) return;
    var quiz = _state.quiz;
    var step = _state.step;
    var total = quiz.steps.length;

    if (step === -1)                                              return _renderIntro();
    if (step < total)                                             return _renderQuestion(step);
    if (step === total && quiz.email && quiz.email.enabled)       return _renderEmail();
    _runRecommendation();
  }

  // ── INTRO ───────────────────────────────────────────────

  function _renderIntro() {
    var intro = _state.quiz.intro;
    var h = '<div class="cxq__intro">';
    if (intro.image) h += '<img src="' + intro.image + '" class="cxq__intro-img" alt="">';
    h += '<h2 class="cxq__title">' + intro.headline + '</h2>';
    if (intro.subtext) h += '<p class="cxq__subtitle">' + intro.subtext + '</p>';
    h += '<button class="cxq__btn cxq__btn--primary cxq__start">' + (intro.cta || 'Comenzar') + '</button>';
    h += '</div>';
    _content.innerHTML = h;

    _content.querySelector('.cxq__start').addEventListener('click', function() {
      _state.step = 0;
      _render();
    });
  }

  // ── QUESTION ────────────────────────────────────────────

  function _renderQuestion(idx) {
    var quiz = _state.quiz;
    var stepCfg = quiz.steps[idx];
    var total = quiz.steps.length;
    var pct = ((idx + 1) / total * 100).toFixed(0);

    var h = '<div class="cxq__step">';
    // Progress bar
    h += '<div class="cxq__progress"><div class="cxq__progress-bar" style="width:' + pct + '%"></div></div>';
    h += '<p class="cxq__count">' + (idx + 1) + ' / ' + total + '</p>';
    h += '<h2 class="cxq__question">' + stepCfg.question + '</h2>';

    // Options
    h += '<div class="cxq__options">';
    var currentAnswer = _state.answers[stepCfg.id];
    for (var i = 0; i < stepCfg.options.length; i++) {
      var opt = stepCfg.options[i];
      var sel = false;
      if (stepCfg.type === 'multi') {
        sel = (currentAnswer || []).indexOf(opt.key) !== -1;
      } else {
        sel = currentAnswer === opt.key;
      }
      h += '<button class="cxq__pill' + (sel ? ' is-selected' : '') + '" data-key="' + opt.key + '">';
      if (opt.icon) h += '<span class="cxq__pill-icon">' + opt.icon + '</span>';
      h += '<span>' + opt.label + '</span>';
      h += '</button>';
    }
    h += '</div>';

    // Navigation
    h += '<div class="cxq__nav">';
    if (idx > 0) {
      h += '<button class="cxq__btn cxq__btn--ghost cxq__prev">\u2190 Atr\u00e1s</button>';
    }
    if (stepCfg.type === 'multi') {
      h += '<button class="cxq__btn cxq__btn--primary cxq__next">Continuar</button>';
    }
    h += '</div>';
    h += '</div>';

    _content.innerHTML = h;

    // Bind pill clicks
    var pills = _content.querySelectorAll('.cxq__pill');
    for (var j = 0; j < pills.length; j++) {
      pills[j].addEventListener('click', function() {
        var key = this.getAttribute('data-key');
        if (stepCfg.type === 'multi') {
          var arr = _state.answers[stepCfg.id] || [];
          var k = arr.indexOf(key);
          if (k === -1) arr.push(key); else arr.splice(k, 1);
          _state.answers[stepCfg.id] = arr;
          this.classList.toggle('is-selected');
        } else {
          _state.answers[stepCfg.id] = key;
          _state.step++;
          _render();
        }
      });
    }

    // Back button
    var prevBtn = _content.querySelector('.cxq__prev');
    if (prevBtn) {
      prevBtn.addEventListener('click', function() {
        _state.step--;
        _render();
      });
    }

    // Continue button (multi-select)
    var nextBtn = _content.querySelector('.cxq__next');
    if (nextBtn) {
      nextBtn.addEventListener('click', function() {
        _state.step++;
        _render();
      });
    }
  }

  // ── EMAIL CAPTURE ───────────────────────────────────────

  function _renderEmail() {
    var cfg = _state.quiz.email;
    var h = '<div class="cxq__email">';
    h += '<h2 class="cxq__title">' + (cfg.headline || 'Recibe tus resultados') + '</h2>';
    if (cfg.subtext) h += '<p class="cxq__subtitle">' + cfg.subtext + '</p>';
    h += '<input type="email" class="cxq__input" placeholder="' + (cfg.placeholder || 'tu@email.com') + '" id="cxq-email-input">';
    // Marketing consent. UNCHECKED by default and never auto-selected: a pre-ticked box is not
    // consent, it is an assumption. Giving an email to see your own quiz result is a different
    // act from agreeing to receive marketing, so the two are asked separately.
    h += '<label class="cxq__consent" for="cxq-consent" style="display:flex;align-items:flex-start;gap:8px;margin:14px 0 4px;font-size:13px;line-height:1.45;text-align:left;cursor:pointer">';
    h += '<input type="checkbox" id="cxq-consent" class="cxq__consent-box" style="margin-top:2px;flex-shrink:0">';
    h += '<span>' + (cfg.consent_label || 'Quiero recibir consejos de salud celular y ofertas de CellX por correo. Puedo darme de baja cuando quiera.') + '</span>';
    h += '</label>';
    // LFPDPPP arts. 15-17: the privacy notice must be available WHERE the data is collected.
    // This quiz is a full-screen modal (body overflow is hidden), so the site footer's link is
    // unreachable from here. target=_blank so opening it does not destroy in-progress quiz state.
    h += '<p class="cxq__privacy" style="margin:2px 0 12px;font-size:12px;opacity:.75;text-align:left">'
      + '<a href="/policies/privacy-policy" target="_blank" rel="noopener">Aviso de privacidad</a></p>';
    h += '<button class="cxq__btn cxq__btn--primary cxq__email-submit">' + (cfg.cta || 'Ver resultados') + '</button>';
    h += '<button class="cxq__btn cxq__btn--ghost cxq__email-skip">' + (cfg.skip_label || 'Omitir') + '</button>';
    h += '</div>';
    _content.innerHTML = h;

    var input = document.getElementById('cxq-email-input');

    _content.querySelector('.cxq__email-submit').addEventListener('click', function() {
      var email = input.value.trim();
      if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        input.style.borderColor = '#e53e3e';
        input.focus();
        return;
      }
      var consentBox = document.getElementById('cxq-consent');
      // Absent checkbox must read as NO consent, never as yes.
      var wantsMarketing = !!(consentBox && consentBox.checked);
      _state.email = email;
      _state.marketingConsent = wantsMarketing;
      _trackEmail(email, wantsMarketing);
      _state.step++;
      _render();
    });

    _content.querySelector('.cxq__email-skip').addEventListener('click', function() {
      _state.step++;
      _render();
    });

    input.addEventListener('keydown', function(e) {
      if (e.key === 'Enter') _content.querySelector('.cxq__email-submit').click();
    });
  }

  /**
   * Two DIFFERENT things happen here, and conflating them is what broke this before.
   *
   * `identify` + `track` are TRANSACTIONAL: the person asked to see their own quiz result, so we
   * record who they are and what they answered in order to deliver it. That runs either way.
   *
   * Marketing subscription is CONSENT, and it only happens when they actively ticked the box.
   * Measured 2026-07-17: of 69 general-quiz profiles, 100% had an email but only 49% carried
   * marketing consent — because `identify` sets an email and grants nothing. The other 51% were
   * captured-but-unreachable. This closes that gap going forward, WITHOUT retroactively
   * subscribing anyone who never agreed.
   */
  function _trackEmail(email, wantsMarketing) {
    var quiz = _state.quiz;
    // Klaviyo integration (works when Klaviyo JS is loaded on the page)
    if (window._learnq && quiz.email) {
      // The consent flag goes on the PROFILE, not only the event. The `track` below fires for
      // everyone including people who declined, and this repo already contains five nurture
      // sequences (klaviyo/{cognitive,beauty,sleep,athletic,immune}-sequence.md) whose documented
      // trigger is a quiz-completion metric. Today no flow listens to this metric, but that safety
      // lives in dashboard state any teammate can change in two clicks. A profile property is
      // referenceable from a flow trigger filter; an event property on a past event is not.
      // See CLAUDE.md: any flow triggered by a quiz metric MUST filter quiz_marketing_consent.
      window._learnq.push(['identify', { '$email': email, 'quiz_marketing_consent': !!wantsMarketing }]);
      window._learnq.push(['track', quiz.email.klaviyo_event || 'Quiz Completed', {
        quiz_id: quiz.id,
        answers: _state.answers,
        marketing_consent: !!wantsMarketing
      }]);
    }
    if (wantsMarketing) _subscribeToMarketing(email);
  }

  /**
   * Record explicit marketing consent via Klaviyo's client-side subscriptions endpoint.
   * `company_id` is the PUBLIC site key (already exposed by Klaviyo's own onsite JS) — the
   * private API key is never used in the browser.
   *
   * Failures are swallowed on purpose: a subscription that does not land must never stop someone
   * from seeing the results they came for. It is logged so a silent failure is still findable.
   */
  function _subscribeToMarketing(email) {
    var COMPANY_ID = 'Vb8RQe';
    var LIST_ID = 'Uw6P56';
    try {
      fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + COMPANY_ID, {
        method: 'POST',
        // Klaviyo retires a revision ~2 years after release (1yr stable + 1yr deprecated), and a
        // retired revision returns 404 — verified by control probe: revision '1999-01-01' →
        // 404 "Revision date requested is before the earliest available". Since subscribe failures
        // are swallowed here so they never block a user's results, a silent 404 would stop consent
        // recording with nobody noticing. '2025-01-15' retires ~2027-01; the payload shape is
        // identical in the current stable revision, so pin forward.
        headers: { 'Content-Type': 'application/json', 'revision': '2026-07-15' },
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              custom_source: 'CellX Quiz',
              profile: {
                data: {
                  type: 'profile',
                  attributes: {
                    email: email,
                    subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } }
                  }
                }
              }
            },
            relationships: { list: { data: { type: 'list', id: LIST_ID } } }
          }
        })
      }).then(function (r) {
        if (!r.ok) console.warn('[CellXQuiz] consent subscribe failed:', r.status);
      }).catch(function (e) { console.warn('[CellXQuiz] consent subscribe error:', e); });
    } catch (e) {
      console.warn('[CellXQuiz] consent subscribe threw:', e);
    }
  }

  // ── RECOMMENDATION ──────────────────────────────────────

  function _runRecommendation() {
    _content.innerHTML =
      '<div class="cxq__loading">' +
        '<div class="cxq__spinner"></div>' +
        '<p>Analizando tus respuestas\u2026</p>' +
      '</div>';

    var result;
    try {
      result = _state.quiz.recommend(_state.answers);
    } catch (e) {
      console.error('[CellXQuiz] Recommendation error:', e);
      result = null;
    }
    _state.result = result;

    if (!result || !result.products || result.products.length === 0) {
      return _renderEmpty();
    }

    // Fetch product data from Shopify AJAX API
    var fetches = result.products.map(function(handle) {
      return fetch('/products/' + handle + '.js')
        .then(function(r) { return r.ok ? r.json() : null; })
        .catch(function() { return null; });
    });

    Promise.all(fetches).then(function(products) {
      _state.products = products.filter(Boolean);
      if (_state.products.length === 0) return _renderEmpty();
      _renderResults();
    });
  }

  function _renderEmpty() {
    var h = '<div class="cxq__empty">';
    h += '<h2 class="cxq__title">No encontramos una recomendaci\u00f3n espec\u00edfica</h2>';
    h += '<p class="cxq__subtitle">Explora nuestro cat\u00e1logo completo para encontrar lo ideal para ti.</p>';
    h += '<a href="/collections/todos-los-suplementos" class="cxq__btn cxq__btn--primary">Ver suplementos</a>';
    h += '<button class="cxq__btn cxq__btn--ghost cxq__retake">Volver a empezar</button>';
    h += '</div>';
    _content.innerHTML = h;

    var retake = _content.querySelector('.cxq__retake');
    if (retake) {
      retake.addEventListener('click', function() {
        _resetState();
        _render();
      });
    }
  }

  // ── RESULTS ─────────────────────────────────────────────

  function _renderResults() {
    var quiz = _state.quiz;
    var result = _state.result;
    var products = _state.products;
    var cfg = quiz.results || {};

    var h = '<div class="cxq__results">';
    h += '<h2 class="cxq__title">' + (result.headline || 'Tu recomendaci\u00f3n') + '</h2>';
    if (result.subtext) h += '<p class="cxq__subtitle">' + result.subtext + '</p>';
    if (result.insight) h += '<p class="cxq__insight">' + result.insight + '</p>';

    // Product cards
    h += '<div class="cxq__products">';
    for (var i = 0; i < products.length; i++) {
      var p = products[i];
      var v = _firstAvailable(p);
      var img = (p.images && p.images[0]) ? _resize(p.images[0], '200x') : '';

      h += '<div class="cxq__card">';
      if (img) h += '<img src="' + img + '" alt="' + _esc(p.title) + '" class="cxq__card-img" loading="lazy">';
      h += '<div class="cxq__card-info">';
      h += '<h3 class="cxq__card-name">' + _esc(p.title) + '</h3>';

      if (v) {
        h += '<div class="cxq__card-price">';
        if (v.compare_at_price && v.compare_at_price > v.price) {
          h += '<s class="cxq__card-compare">' + _money(v.compare_at_price) + '</s> ';
        }
        h += '<span>' + _money(v.price) + '</span>';
        h += '</div>';
        h += '<button class="cxq__btn cxq__btn--primary cxq__btn--sm cxq__atc" data-vid="' + v.id + '">' + (cfg.cta_single || 'Agregar') + '</button>';
      } else {
        h += '<span class="cxq__sold-out">Agotado</span>';
      }
      h += '</div></div>';
    }
    h += '</div>';

    // Bundle "Add all" CTA
    var allVariants = [];
    for (var j = 0; j < products.length; j++) {
      var av = _firstAvailable(products[j]);
      if (av) allVariants.push(av.id);
    }
    if (allVariants.length > 1) {
      h += '<button class="cxq__btn cxq__btn--primary cxq__btn--full cxq__atc-all" data-vids=\'' + JSON.stringify(allVariants) + '\'>' + (cfg.cta_all || 'Agregar todo al carrito') + '</button>';
    }

    h += '<button class="cxq__btn cxq__btn--ghost cxq__retake">' + (cfg.retake_label || 'Volver a empezar') + '</button>';
    h += '</div>';

    _content.innerHTML = h;
    _bindResultsEvents(cfg);
  }

  function _bindResultsEvents(cfg) {
    // Single ATC buttons
    var atcBtns = _content.querySelectorAll('.cxq__atc');
    for (var i = 0; i < atcBtns.length; i++) {
      atcBtns[i].addEventListener('click', function() {
        _addToCart([{ id: parseInt(this.getAttribute('data-vid')), quantity: 1 }], this);
      });
    }

    // Add all button
    var allBtn = _content.querySelector('.cxq__atc-all');
    if (allBtn) {
      allBtn.addEventListener('click', function() {
        var ids = JSON.parse(this.getAttribute('data-vids'));
        var items = [];
        for (var k = 0; k < ids.length; k++) {
          items.push({ id: ids[k], quantity: 1 });
        }
        _addToCart(items, this);
      });
    }

    // Retake button
    var retakeBtn = _content.querySelector('.cxq__retake');
    if (retakeBtn) {
      retakeBtn.addEventListener('click', function() {
        _resetState();
        _render();
      });
    }
  }

  // ── CART INTEGRATION ────────────────────────────────────
  // Matches the Flux theme pattern: request cart-drawer + cart-icon-bubble
  // section re-renders from Shopify, then call renderContents() to update
  // the drawer DOM and open it.

  function _addToCart(items, btn) {
    var originalText = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'Agregando\u2026';

    // Find the theme's cart element (cart-drawer or cart-notification)
    var cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');

    // Build request body with sections for cart drawer re-rendering
    var body = { items: items };
    if (cart && typeof cart.getSectionsToRender === 'function') {
      var sections = cart.getSectionsToRender();
      var sectionIds = [];
      for (var i = 0; i < sections.length; i++) {
        sectionIds.push(sections[i].id);
      }
      body.sections = sectionIds.join(',');
      body.sections_url = window.location.pathname;
    }

    var cartUrl = '/cart/add.js';

    fetch(cartUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    })
    .then(function(r) {
      if (!r.ok) throw new Error('Cart error ' + r.status);
      return r.json();
    })
    .then(function(response) {
      btn.textContent = '\u2713 Agregado';
      btn.classList.add('is-added');

      // Publish cart update event for other theme components
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'quiz' });
      }

      // Update cart drawer with re-rendered sections + open it
      if (cart && typeof cart.renderContents === 'function' && response.sections) {
        close();
        cart.renderContents(response);
      } else if (cart && typeof cart.open === 'function') {
        close();
        cart.open();
      }
    })
    .catch(function(err) {
      console.error('[CellXQuiz] Cart error:', err);
      btn.disabled = false;
      btn.textContent = originalText;
    });
  }

  // ── STATE HELPERS ───────────────────────────────────────

  function _resetState() {
    if (!_state) return;
    _state.step = _state.quiz.intro ? -1 : 0;
    _state.answers = {};
    _state.result = null;
    _state.products = [];
    _state.email = '';
  }

  // ── DOM HELPERS ─────────────────────────────────────────

  function _firstAvailable(product) {
    if (!product || !product.variants) return null;
    for (var i = 0; i < product.variants.length; i++) {
      if (product.variants[i].available) return product.variants[i];
    }
    return null;
  }

  function _money(cents) {
    return '$' + Math.round(cents / 100).toLocaleString('es-MX') + ' MXN';
  }

  function _resize(url, size) {
    if (!url) return '';
    return url.replace(/(\.\w+)(\?|$)/, '_' + size + '$1$2');
  }

  function _esc(str) {
    if (!str) return '';
    var d = document.createElement('div');
    d.textContent = str;
    return d.innerHTML;
  }

  // ── KEYBOARD ────────────────────────────────────────────

  document.addEventListener('keydown', function(e) {
    if (e.key === 'Escape' && _state) close();
  });

  // ── EXPOSE ──────────────────────────────────────────────

  window.CellXQuiz = { register: register, open: open, close: close };

  // Backward compat: openCellXQuiz() opens the 'general' quiz
  window.openCellXQuiz = function() { open('general'); };

})();
